import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtErr, wsUrl } from "../lib/api";
import { useAuth } from "./AuthContext";
import { logSwallow } from "../lib/log";
import IncomingCall from "../components/app/IncomingCall";
import VideoCall from "../components/app/VideoCall";

const CallCtx = createContext(null);
export const useCall = () => useContext(CallCtx);

// Global 1:1 call state machine + signaling over /api/ws/user.
// Phases: null (idle) | outgoing (caller ringing) | incoming (callee ringing) | active (connected).
export function CallProvider({ children }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [call, setCall] = useState(null);
  const callRef = useRef(null);
  callRef.current = call;
  const ringTimerRef = useRef(null);

  const clearRing = () => { if (ringTimerRef.current) { clearTimeout(ringTimerRef.current); ringTimerRef.current = null; } };
  const endCall = useCallback(() => { clearRing(); setCall(null); }, []);

  // Caller: begin an outbound call for a match.
  const startCall = useCallback(async (matchId, personName) => {
    if (callRef.current) return; // never overlap sessions
    try {
      const { data } = await api.post(`/matches/${matchId}/call`);
      setCall({
        phase: "outgoing", callId: data.call_id, matchId,
        roomUrl: data.room_url, token: data.token,
        person: personName ? { display_name: personName } : null,
      });
      clearRing();
      ringTimerRef.current = setTimeout(async () => {
        // No answer → cancel and inform the user.
        try { await api.post(`/calls/${data.call_id}/end`); } catch (e) { logSwallow("call.timeout.end", e); }
        endCall();
        toast(t("call.noAnswer"));
      }, (data.ring_ttl || 45) * 1000);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [endCall, t]);

  const acceptCall = useCallback(async () => {
    const c = callRef.current;
    if (!c || c.phase !== "incoming") return;
    clearRing();
    try {
      const { data } = await api.post(`/calls/${c.callId}/accept`);
      setCall({ phase: "active", callId: c.callId, matchId: c.matchId, roomUrl: data.room_url, token: data.token, person: c.person });
    } catch (e) { toast.error(fmtErr(e)); endCall(); }
  }, [endCall]);

  const declineCall = useCallback(async (reason = "declined") => {
    const c = callRef.current;
    if (!c) return;
    clearRing();
    try { await api.post(`/calls/${c.callId}/decline?reason=${reason}`); } catch (e) { logSwallow("call.decline", e); }
    endCall();
  }, [endCall]);

  const hangUp = useCallback(async () => {
    const c = callRef.current;
    if (!c) return;
    clearRing();
    try { await api.post(`/calls/${c.callId}/end`); } catch (e) { logSwallow("call.end", e); }
    endCall();
  }, [endCall]);

  // WS listener for incoming/accepted/declined/cancelled/ended.
  useEffect(() => {
    if (!user?.id) return undefined;
    let ws;
    let closed = false;
    const open = () => {
      const url = wsUrl("/user");
      if (!url) return;
      try { ws = new WebSocket(url); } catch (e) { logSwallow("call.ws.open", e); return; }
      ws.onopen = async () => {
        // Recover a live inbound ring missed during WS warm-up or a page refresh.
        if (callRef.current) return;
        try {
          const { data } = await api.get("/calls/active");
          const a = data.call;
          if (a && a.role === "callee" && a.status === "ringing" && !callRef.current) {
            setCall({ phase: "incoming", callId: a.call_id, matchId: a.match_id, person: a.caller || null });
            clearRing();
            ringTimerRef.current = setTimeout(() => { declineCall("timeout"); }, (a.ring_ttl || 45) * 1000);
          }
        } catch (e) { logSwallow("call.active", e); }
      };
      ws.onmessage = (ev) => {
        let m; try { m = JSON.parse(ev.data); } catch { return; }
        const c = callRef.current;
        if (m.type === "call.incoming") {
          if (c) { // busy → auto-decline so caller isn't left ringing
            api.post(`/calls/${m.call_id}/decline?reason=busy`).catch(() => {});
            return;
          }
          setCall({ phase: "incoming", callId: m.call_id, matchId: m.match_id, person: m.caller || null });
          clearRing();
          ringTimerRef.current = setTimeout(() => { declineCall("timeout"); }, (m.ring_ttl || 45) * 1000);
        } else if (m.type === "call.accepted") {
          if (c && c.phase === "outgoing" && c.callId === m.call_id) {
            clearRing();
            setCall({ phase: "active", callId: c.callId, matchId: c.matchId, roomUrl: c.roomUrl, token: c.token, person: c.person });
          }
        } else if (m.type === "call.declined") {
          if (c && c.callId === m.call_id) { endCall(); toast(m.reason === "timeout" ? t("call.noAnswer") : t("call.declinedByOther")); }
        } else if (m.type === "call.cancelled") {
          if (c && c.callId === m.call_id) { endCall(); toast(t("call.cancelledByOther")); }
        } else if (m.type === "call.ended") {
          if (c && c.callId === m.call_id) { endCall(); toast(t("call.ended")); }
        }
      };
      ws.onerror = (e) => logSwallow("call.ws.error", e);
      ws.onclose = () => { if (!closed) setTimeout(open, 2000); };
    };
    open();
    return () => { closed = true; if (ws) { try { ws.close(); } catch (e) { logSwallow("call.ws.close", e); } } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <CallCtx.Provider value={{ startCall, call }}>
      {children}
      {call?.phase === "incoming" && (
        <IncomingCall mode="incoming" person={call.person} onAccept={acceptCall} onDecline={() => declineCall("declined")} />
      )}
      {call?.phase === "outgoing" && (
        <IncomingCall mode="outgoing" person={call.person} onCancel={hangUp} />
      )}
      {call?.phase === "active" && (
        <VideoCall roomUrl={call.roomUrl} token={call.token} onLeave={hangUp} />
      )}
    </CallCtx.Provider>
  );
}
